import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './sections.module.css';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { publicSiteAPI } from '../../../services/api';
import { DemographicProfile, StakeholderAdvies, GeoJsonPolygon, GeoJsonPoint, OmgevingsData, BedrijvenData, BedrijvenAdvies, VoorzieningenData, BedrijvenOverpassData, OrganisatiesData } from '../../../types';
import { OmgevingsDataLayer } from '../../gebiedsanalyse/OmgevingsDataLayer';
import { GebiedsdataLayer } from '../../gebiedsanalyse/GebiedsdataLayer';
import { PandenLayer } from '../../gebiedsanalyse/PandenLayer';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface GebiedsanalyseSectionProps {
  slug: string;
}

interface AreaData {
  boundary: GeoJsonPolygon;
  bufferZone: GeoJsonPolygon;
  location: GeoJsonPoint;
  influenceRadius: number;
  demographics: DemographicProfile | null;
  buurtNaam: string | null;
  adviezen: StakeholderAdvies[];
  bedrijven?: BedrijvenData | null;
  bedrijvenAdviezen?: BedrijvenAdvies[];
  voorzieningen?: VoorzieningenData | null;
  bedrijvenOverpass?: BedrijvenOverpassData | null;
  organisaties?: OrganisatiesData | null;
}

const SEVERITY_COLORS: Record<string, string> = {
  high: '#1E3A2F',
  medium: '#FF6B35',
  low: '#277CEA',
};

const BEDRIJVEN_CATEGORIES = [
  { key: 'kantoren', label: 'Kantoren', color: '#277CEA' },
  { key: 'retail', label: 'Retail', color: '#1E3A2F' },
  { key: 'horeca', label: 'Horeca', color: '#FF6B35' },
  { key: 'zorg', label: 'Zorg', color: '#00C853' },
  { key: 'onderwijs', label: 'Onderwijs', color: '#9C27B0' },
  { key: 'industrie', label: 'Industrie', color: '#9DA6A8' },
  { key: 'sport', label: 'Sport', color: '#0ea5e9' },
  { key: 'overig', label: 'Overig', color: '#bbb' },
] as const;

const FILTER_CONFIG = [
  { key: 'werkzaamheden', label: 'Werkzaamheden', color: '#f97316' },
  { key: 'vergunningen', label: 'Vergunningen', color: '#10b981' },
  { key: 'voorzieningen', label: 'Voorzieningen', color: '#9C27B0' },
  { key: 'bedrijven_osm', label: 'Bedrijven', color: '#277CEA' },
  { key: 'organisaties', label: 'Organisaties', color: '#00BCD4' },
  { key: 'panden', label: 'Panden (BAG)', color: '#795548' },
];

export const GebiedsanalyseSection: React.FC<GebiedsanalyseSectionProps> = ({ slug }) => {
  const ref = useScrollReveal();
  const [data, setData] = useState<AreaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [omgevingsData, setOmgevingsData] = useState<OmgevingsData | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, boolean>>({
    werkzaamheden: true,
    vergunningen: true,
    voorzieningen: false,
    bedrijven_osm: false,
    organisaties: false,
    panden: false,
  });

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      const [areaResult, omgevingsResult] = await Promise.allSettled([
        publicSiteAPI.getGebiedsanalyse(slug),
        publicSiteAPI.getOmgevingsdata(slug),
      ]);
      if (areaResult.status === 'fulfilled') setData(areaResult.value);
      if (omgevingsResult.status === 'fulfilled') setOmgevingsData(omgevingsResult.value);
    } catch {
      // No data available
    } finally {
      setLoading(false);
    }
  };

  const handleFilterToggle = useCallback((key: string) => {
    setActiveFilters(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (loading) {
    return (
      <section id="gebiedsanalyse" className={styles.section} ref={ref}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Gebiedsanalyse</h2>
          <p className={styles.sectionSubtitle}>Data laden...</p>
        </div>
      </section>
    );
  }

  if (!data || !data.boundary) {
    return null;
  }

  const { boundary, bufferZone, location, demographics, buurtNaam, adviezen, bedrijven, bedrijvenAdviezen } = data;
  const voorzieningen = data.voorzieningen as VoorzieningenData | null;
  const bedrijvenOverpass = data.bedrijvenOverpass as BedrijvenOverpassData | null;
  const organisaties = data.organisaties as OrganisatiesData | null;

  const mapCenter: [number, number] = location
    ? [location.coordinates[1], location.coordinates[0]]
    : [52.1326, 5.2913];

  const boundaryStyle = {
    color: '#1E3A2F',
    weight: 3,
    fillColor: '#1E3A2F',
    fillOpacity: 0.2,
  };

  const bufferStyle = {
    color: '#1E3A2F',
    weight: 1,
    fillColor: '#1E3A2F',
    fillOpacity: 0.06,
    dashArray: '5, 10',
  };

  const totalWoningen = demographics
    ? (demographics.huurwoningen || 0) + (demographics.koopwoningen || 0)
    : 0;
  const huurPercentage = totalWoningen > 0 ? Math.round(((demographics?.huurwoningen || 0) / totalWoningen) * 100) : 0;

  // Count items per filter for display
  const filterCounts: Record<string, number> = {
    werkzaamheden: omgevingsData?.werkzaamheden?.length || 0,
    vergunningen: omgevingsData?.vergunningen?.length || 0,
    voorzieningen: voorzieningen?.items?.length || 0,
    bedrijven_osm: bedrijvenOverpass?.items?.length || 0,
    organisaties: organisaties?.items?.length || 0,
    panden: 0, // Panden are loaded dynamically by PandenLayer
  };

  const totalOmgevingsItems = Object.values(filterCounts).reduce((a, b) => a + b, 0) + 1; // +1 for panden (always available)

  return (
    <section id="gebiedsanalyse" className={styles.section} ref={ref}>
      <div className={styles.container}>
        <h2 className={styles.sectionTitle}>Gebiedsanalyse</h2>
        {buurtNaam && (
          <p className={styles.sectionSubtitle}>
            Demografische informatie over {buurtNaam} en omgeving
          </p>
        )}

        {/* Omgevingsdata Filter Toggles */}
        {totalOmgevingsItems > 0 && (
          <div className={styles.omgevingsFilterBar}>
            {FILTER_CONFIG.map(filter => {
              const count = filterCounts[filter.key] || 0;
              const active = activeFilters[filter.key];
              return (
                <button
                  key={filter.key}
                  className={`${styles.omgevingsToggle} ${active ? styles.omgevingsToggleActive : ''}`}
                  onClick={() => handleFilterToggle(filter.key)}
                  style={{
                    borderColor: active ? filter.color : undefined,
                    color: active ? filter.color : undefined,
                  }}
                >
                  <span
                    className={styles.omgevingsToggleDot}
                    style={{ backgroundColor: active ? filter.color : '#ccc' }}
                  />
                  {filter.label}
                  {count > 0 && (
                    <span
                      className={styles.omgevingsToggleCount}
                      style={{ backgroundColor: active ? filter.color : '#ccc' }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Map */}
        <div className={styles.gebiedsanalyseMap}>
          <MapContainer
            center={mapCenter}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            zoomControl={true}
          >
            <LayersControl position="topleft">
              <LayersControl.BaseLayer checked name="Kaart">
                <TileLayer
                  url="https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/grijs/EPSG:3857/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.pdok.nl/">PDOK</a>'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Luchtfoto">
                <TileLayer
                  url="https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg"
                  attribution='&copy; <a href="https://www.pdok.nl/">PDOK</a>'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
            </LayersControl>

            {bufferZone && (
              <GeoJSON
                data={{ type: 'Feature', properties: {}, geometry: bufferZone } as any}
                style={bufferStyle}
              />
            )}
            {boundary && (
              <GeoJSON
                data={{ type: 'Feature', properties: {}, geometry: boundary } as any}
                style={boundaryStyle}
              />
            )}

            <OmgevingsDataLayer
              data={omgevingsData}
              activeFilters={activeFilters}
            />
            <GebiedsdataLayer
              voorzieningen={activeFilters.voorzieningen ? (voorzieningen?.items || null) : null}
              bedrijven={activeFilters.bedrijven_osm ? (bedrijvenOverpass?.items || null) : null}
              organisaties={activeFilters.organisaties ? (organisaties?.items || null) : null}
              activeFilters={activeFilters}
            />
            <PandenLayer active={activeFilters.panden} />
          </MapContainer>
        </div>

        {/* Stats Grid */}
        {demographics && (
          <div className={styles.gebiedsanalyseStats}>
            <div className={styles.gebiedsStat}>
              <span className={styles.gebiedsStatValue}>{demographics.inwoners.toLocaleString('nl-NL')}</span>
              <span className={styles.gebiedsStatLabel}>Inwoners</span>
            </div>
            <div className={styles.gebiedsStat}>
              <span className={styles.gebiedsStatValue}>{demographics.percentage65Plus}%</span>
              <span className={styles.gebiedsStatLabel}>65+</span>
            </div>
            <div className={styles.gebiedsStat}>
              <span className={styles.gebiedsStatValue}>{huurPercentage}%</span>
              <span className={styles.gebiedsStatLabel}>Huurwoningen</span>
            </div>
          </div>
        )}

        {/* Interne analysedata (omgevingsdata, leeftijdsverdeling, communicatie-inzichten, bedrijvigheid)
            wordt NIET getoond op de publieke projectwebsite — deze data is alleen beschikbaar
            in de interne gebiedsanalyse module */}
      </div>
    </section>
  );
};
